import React, { useEffect, useRef } from "react";

/*
 * Decorative "moving dots" background — soft green/amber bubbles that slowly
 * rise and respawn, matching the animation used across the site's hero bands
 * (AboutHero). Renders an absolutely-positioned canvas that fills its nearest
 * positioned parent, so drop it inside a `position: relative` container and
 * keep the real content above it (z-index). JS-driven (requestAnimationFrame)
 * so it keeps moving even when the OS "reduce motion" setting freezes CSS.
 */
const GREEN = "127,180,50";
const AMBER = "235,147,9";

export default function MovingDots({ className = "", light = false }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let w = 0;
    let h = 0;
    let raf = 0;
    let bubbles = [];

    const reset = (b, initial) => {
      b.r = 6 + Math.random() * 16;
      b.x = Math.random() * w;
      b.y = initial ? Math.random() * h : h + b.r + Math.random() * 30;
      b.sp = 0.25 + Math.random() * 0.5;
      b.vx = (Math.random() - 0.5) * 0.25;
      b.col = Math.random() > 0.5 ? (light ? "255,255,255" : GREEN) : (light ? "255,238,208" : AMBER);
      return b;
    };

    const seed = () => {
      const count = Math.max(8, Math.min(20, Math.round(w / 95)));
      bubbles = [];
      for (let i = 0; i < count; i++) bubbles.push(reset({}, true));
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const frame = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < bubbles.length; i++) {
        const b = bubbles[i];
        b.y -= b.sp;
        b.x += b.vx;
        if (b.y < -b.r - 4 || b.x < -b.r || b.x > w + b.r) {
          reset(b, false);
          continue;
        }
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${b.col},0.07)`;
        ctx.fill();
        ctx.lineWidth = 1.2;
        ctx.strokeStyle = `rgba(${b.col},0.3)`;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(b.x - b.r * 0.32, b.y - b.r * 0.32, Math.max(1, b.r * 0.16), 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    };

    resize();
    seed();
    frame();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [light]);

  return <canvas className={("moving-dots " + className).trim()} ref={canvasRef} aria-hidden="true" />;
}
